import React, { useState, useEffect } from 'react';
import { useProviders } from '../hooks/useProviders';
import { useConversation } from '../hooks/useConversation';
import { ProviderSelector } from '../components/ProviderSelector';
import { LanguageSelector } from '../components/LanguageSelector';
import { ChatView } from '../components/ChatView';
import { RecorderButton } from '../components/RecorderButton';

export function ComparePage() {
  const { sttProviders, ttsProviders, loading } = useProviders();
  const [sttProvider, setSttProvider] = useState('');
  const [ttsProvider, setTtsProvider] = useState('');
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    if (sttProviders.length > 0 && !sttProvider) {
      const first = sttProviders.find((p) => !p.disabled);
      if (first) setSttProvider(first.name);
    }
  }, [sttProviders, sttProvider]);

  useEffect(() => {
    if (ttsProviders.length > 0 && !ttsProvider) {
      const first = ttsProviders.find((p) => !p.disabled);
      if (first) setTtsProvider(first.name);
    }
  }, [ttsProviders, ttsProvider]);

  const { messages, state, error, startVoiceInput, stopVoiceInput } =
    useConversation(sttProvider, ttsProvider, language);

  return (
    <div style={styles.page}>
      <div style={styles.intro}>
        <h2 style={styles.title}>Compare providers</h2>
        <p style={styles.subtitle}>
          Speak once and hear your message run end-to-end through the chosen
          Speech-to-Text → LLM → Text-to-Speech stack. Pick the providers and a
          language below, hold the mic button to talk, then expand the metrics
          under each reply to compare latency and cost across providers.
        </p>
      </div>
      {loading ? (
        <div style={styles.loading}>Loading providers...</div>
      ) : (
        <div style={styles.selectors}>
          <div style={styles.row}>
            <LanguageSelector value={language} onChange={setLanguage} />
          </div>
          <div style={styles.row}>
            <ProviderSelector label="STT" providers={sttProviders} value={sttProvider} onChange={setSttProvider} />
            <ProviderSelector label="TTS" providers={ttsProviders} value={ttsProvider} onChange={setTtsProvider} />
          </div>
        </div>
      )}
      <ChatView messages={messages} />
      {error && <div style={styles.error}>{error}</div>}
      <div style={styles.footer}>
        <RecorderButton state={state} onPressStart={startVoiceInput} onPressEnd={stopVoiceInput} />
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex', flexDirection: 'column', height: '100%',
  },
  intro: {
    padding: '16px 20px 12px', borderBottom: '1px solid #f0f0f0',
  },
  title: { margin: 0, fontSize: 18, color: '#1a1a1a' },
  subtitle: {
    margin: '6px 0 0', fontSize: 13, lineHeight: 1.5, color: '#666',
    maxWidth: 640,
  },
  selectors: {
    padding: '12px 20px', display: 'flex', flexDirection: 'column',
    gap: 12, borderBottom: '1px solid #f0f0f0',
  },
  row: { display: 'flex', gap: 24, flexWrap: 'wrap' },
  loading: { padding: 20, color: '#888', fontSize: 14 },
  error: {
    margin: '0 20px', padding: '10px 14px', background: '#fff0f0',
    border: '1px solid #fcc', borderRadius: 8, color: '#c00', fontSize: 13,
  },
  footer: { borderTop: '1px solid #eee', display: 'flex', justifyContent: 'center' },
};
